/**
 * FOCO OPERACIONAL AGORA — cockpit Ao Vivo (3 slots fixos).
 */
import React, { useMemo } from 'react';
import { ArrowRight, Flame } from 'lucide-react';
import type {
  OpsBottleneck,
  OpsCriticalProblem,
  OpsOverdueAction,
} from '../../../services/operationsCenterService';
import { EXEC_HERO_CLASS, EXEC_SECTION_LABEL } from './opsExecutiveShared';

export type LiveFocusSlot = 'problem' | 'project' | 'bottleneck';

export interface OpsLiveFocusItem {
  slot: LiveFocusSlot;
  id: string;
  title: string;
  severity: 'critical' | 'warning' | 'info';
  impact: string;
  accumulated: string;
  actionLabel: string;
  onAction: () => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
}

interface OpsLiveFocusBlockProps {
  problems: OpsCriticalProblem[];
  bottlenecks: OpsBottleneck[];
  overdueActions: OpsOverdueAction[];
  projectsOverdue?: number;
  onNavigate: (path: string) => void;
  onBottleneckFocus: (bottleneck: OpsBottleneck) => void;
}

function formatTimeOpen(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins}min aberto`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h aberto`;
  return `${Math.floor(mins / 1440)}d aberto`;
}

function formatDelayMinutes(minutes: number): string {
  if (minutes < 60) return `${Math.round(minutes)}min parado (média)`;
  return `${(minutes / 60).toFixed(1)}h parado (média)`;
}

export function buildLiveFocusItems({
  problems,
  bottlenecks,
  overdueActions,
  projectsOverdue = 0,
  onNavigate,
  onBottleneckFocus,
}: OpsLiveFocusBlockProps): OpsLiveFocusItem[] {
  const items: OpsLiveFocusItem[] = [];

  const critProblem = problems.find((p) => p.severity === 'critical') ?? problems[0];
  if (critProblem) {
    items.push({
      slot: 'problem',
      id: critProblem.id,
      title: critProblem.title,
      severity: critProblem.severity === 'critical' ? 'critical' : 'warning',
      impact: critProblem.verification_overdue
        ? 'Verificação vencida'
        : critProblem.awaiting_verification
          ? 'Contramedidas executadas · aguardando verificação'
        : critProblem.severity === 'critical'
          ? 'Problema crítico · ações ou investigação em aberto'
          : 'Problema aberto requer decisão',
      accumulated: formatTimeOpen(critProblem.created_at),
      actionLabel: 'Abrir problema',
      onAction: () => onNavigate(`/problemas?id=${encodeURIComponent(critProblem.id)}`),
    });
  }

  const delayedWithProject = [...overdueActions]
    .filter((a) => a.project_id)
    .sort((a, b) => b.days_overdue - a.days_overdue)[0];

  if (delayedWithProject?.project_id) {
    items.push({
      slot: 'project',
      id: delayedWithProject.id,
      title: delayedWithProject.title,
      severity: delayedWithProject.days_overdue >= 7 ? 'critical' : 'warning',
      impact: delayedWithProject.project_name ?? 'Ação atrasada de projeto',
      accumulated: `${delayedWithProject.days_overdue}d de atraso`,
      actionLabel: 'Ver ação atrasada',
      onAction: () => onNavigate(`/acoes?actionId=${encodeURIComponent(delayedWithProject.id)}`),
      secondaryLabel: 'Ver projeto',
      onSecondary: () => onNavigate(`/projetos/${encodeURIComponent(delayedWithProject.project_id!)}`),
    });
  } else if (projectsOverdue > 0) {
    const fallbackProject = overdueActions.find((a) => a.project_id);
    if (fallbackProject?.project_id) {
      items.push({
        slot: 'project',
        id: fallbackProject.id,
        title: fallbackProject.title,
        severity: projectsOverdue >= 3 ? 'critical' : 'warning',
        impact: fallbackProject.project_name ?? 'Entregas estratégicas em risco',
        accumulated: `${fallbackProject.days_overdue}d de atraso no portfólio`,
        actionLabel: 'Ver ação atrasada',
        onAction: () => onNavigate(`/acoes?actionId=${encodeURIComponent(fallbackProject.id)}`),
        secondaryLabel: 'Ver projeto',
        onSecondary: () => onNavigate(`/projetos/${encodeURIComponent(fallbackProject.project_id!)}`),
      });
    }
  }

  const topBn = [...bottlenecks].sort(
    (a, b) => b.stuck_count * (b.avg_minutes || 1) - a.stuck_count * (a.avg_minutes || 1),
  )[0];

  if (topBn) {
    items.push({
      slot: 'bottleneck',
      id: topBn.stage_name,
      title: topBn.stage_name,
      severity: topBn.stuck_count >= 5 ? 'critical' : 'warning',
      impact: `${topBn.stuck_count} itens bloqueando o fluxo`,
      accumulated: topBn.avg_minutes > 0 ? formatDelayMinutes(topBn.avg_minutes) : 'Tempo parado elevado',
      actionLabel: 'Ver gargalo',
      onAction: () => onBottleneckFocus(topBn),
    });
  }

  return items.slice(0, 3);
}

export function focusCoversCriticalProblem(items: OpsLiveFocusItem[]): boolean {
  return items.some((i) => i.slot === 'problem');
}

const SEV_BADGE: Record<OpsLiveFocusItem['severity'], string> = {
  critical: 'bg-red-500/15 text-red-600 dark:text-red-400',
  warning: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  info: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
};

const SEV_BORDER: Record<OpsLiveFocusItem['severity'], string> = {
  critical: 'border-l-red-500',
  warning: 'border-l-amber-500',
  info: 'border-l-emerald-500',
};

const SEV_DOT: Record<OpsLiveFocusItem['severity'], string> = {
  critical: 'bg-red-500',
  warning: 'bg-amber-500',
  info: 'bg-emerald-500',
};

const SLOT_LABEL: Record<LiveFocusSlot, string> = {
  problem: 'Problema crítico',
  project: 'Projeto atrasado',
  bottleneck: 'Gargalo principal',
};

export const OpsLiveFocusBlock: React.FC<OpsLiveFocusBlockProps> = (props) => {
  const focusItems = useMemo(() => buildLiveFocusItems(props), [props]);

  const hasCritical = focusItems.some((i) => i.severity === 'critical');
  const hasWarning = focusItems.some((i) => i.severity === 'warning');
  const borderAccent = hasCritical ? 'border-l-red-500' : hasWarning ? 'border-l-amber-500' : 'border-l-emerald-500';
  const headerBg = hasCritical ? 'bg-red-500/[0.06]' : hasWarning ? 'bg-amber-500/[0.05]' : 'bg-emerald-500/[0.05]';

  if (focusItems.length === 0) {
    return (
      <div className="shrink-0 rounded-sm border border-[var(--border-default)]/50 bg-[var(--bg-surface)] px-4 py-2.5">
        <span className={EXEC_SECTION_LABEL}>Foco operacional agora</span>
        <p className="mt-1 text-[12px] text-[var(--text-secondary)]">
          Nenhuma anormalidade relevante neste momento.
        </p>
      </div>
    );
  }

  return (
    <div className={`${EXEC_HERO_CLASS} shrink-0 border-l-[4px] ${borderAccent}`}>
      <div className={`flex items-center gap-2 border-b border-[var(--border-default)]/50 px-4 py-2 ${headerBg}`}>
        <Flame size={16} className={hasCritical ? 'text-red-500' : hasWarning ? 'text-amber-500' : 'text-emerald-500'} />
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--text-primary)]">
          Foco Operacional Agora
        </span>
        <span className="ml-auto text-[9px] font-medium text-[var(--text-muted)]">Até 3 exceções</span>
      </div>

      <div
        className="grid gap-2 p-2"
        style={{ gridTemplateColumns: `repeat(${Math.min(focusItems.length, 3)}, minmax(0, 1fr))` }}
      >
        {focusItems.map((item, index) => (
          <div
            key={item.id}
            className={`group flex min-h-[88px] w-full flex-col rounded-sm border border-[var(--border-default)]/60 bg-[var(--bg-elevated)]/30 px-3 py-2.5 text-left shadow-sm border-l-[3px] ${SEV_BORDER[item.severity]}`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-sm bg-[var(--bg-surface)] text-[10px] font-bold text-[var(--text-secondary)]">
                  {index + 1}
                </span>
                <span className={`rounded-sm px-1.5 py-0.5 text-[7px] font-bold uppercase tracking-wider ${SEV_BADGE[item.severity]}`}>
                  {SLOT_LABEL[item.slot]}
                </span>
              </div>
              <span className={`h-2 w-2 shrink-0 rounded-full ${SEV_DOT[item.severity]}`} title="Severidade" />
            </div>

            <p className="mt-2 line-clamp-2 text-[13px] font-bold leading-snug text-[var(--text-primary)]">
              {item.title}
            </p>
            <p className="mt-0.5 line-clamp-1 text-[10px] font-medium text-[var(--text-secondary)]">
              {item.impact}
            </p>

            <div className="mt-auto flex items-center justify-between gap-2 pt-2">
              <span className="text-[9px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">
                {item.accumulated}
              </span>
              <span className="flex shrink-0 items-center gap-2">
                {item.onSecondary && item.secondaryLabel ? (
                  <button
                    type="button"
                    onClick={item.onSecondary}
                    className="text-[9px] font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                  >
                    {item.secondaryLabel}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={item.onAction}
                  className="flex items-center gap-0.5 text-[9px] font-bold text-[var(--nav-active-bar)]"
                >
                  {item.actionLabel}
                  <ArrowRight size={11} />
                </button>
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OpsLiveFocusBlock;
